import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Building2, ChevronLeft, Loader2, Plus, X } from "lucide-react";
import { API } from "@/constants";
import Disclaimer from "@/components/shared/Disclaimer";
import Footer from "@/components/shared/Footer";

const AttorneyExchangePage = () => {
  const [jobs, setJobs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPostJob, setShowPostJob] = useState(false);
  const [posting, setPosting] = useState(false);
  const [jobForm, setJobForm] = useState({
    title: "",
    description: "",
    job_type: "appearance",
    case_type: "personal_injury",
    jurisdiction: "",
    court: "",
    required_skills: [],
    required_bar_states: [],
    budget_type: "hourly",
    budget_min: 150,
    budget_max: 350,
    urgency: "normal",
    estimated_hours: 10
  });
  
  const JOB_TYPES = [
    { value: "appearance", label: "Court Appearance" },
    { value: "deposition", label: "Deposition" },
    { value: "research", label: "Legal Research" },
    { value: "drafting", label: "Document Drafting" },
    { value: "trial", label: "Trial Support" },
    { value: "full_case", label: "Full Case Handle" }
  ];
  
  const loadData = async () => {
    try {
      const [jobsRes, statsRes] = await Promise.all([
        axios.get(`${API}/exchange/jobs/list?status=open`),
        axios.get(`${API}/exchange/stats`)
      ]);
      setJobs(jobsRes.data.jobs || []);
      setStats(statsRes.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    loadData();
  }, []);
  
  const postJob = async () => {
    setPosting(true);
    try {
      await axios.post(`${API}/exchange/jobs/post`, {
        firm_id: "demo_firm",
        firm_name: "Demo Law Firm",
        ...jobForm
      });
      setShowPostJob(false);
      loadData();
    } catch (e) {
      console.error(e);
    } finally {
      setPosting(false);
    }
  };
  
  const toggleSkill = (skill) => {
    setJobForm(prev => ({
      ...prev,
      required_skills: prev.required_skills.includes(skill)
        ? prev.required_skills.filter(s => s !== skill)
        : [...prev.required_skills, skill]
    }));
  };
  
  const toggleState = (state) => {
    setJobForm(prev => ({
      ...prev,
      required_bar_states: prev.required_bar_states.includes(state)
        ? prev.required_bar_states.filter(s => s !== state)
        : [...prev.required_bar_states, state]
    }));
  };
  
  return (
    <main className="min-h-screen bg-slate-50" data-testid="attorney-exchange-page">
      <Disclaimer />
      <div className="container-shell py-8 max-w-7xl">
        <div className="mb-6">
          <Link to="/tools" className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mb-4">
            <ChevronLeft className="h-4 w-4" /> Back to Tools
          </Link>
          <div className="flex justify-between items-start">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <h1 className="text-3xl font-bold" style={{ fontFamily: "'Fraunces', serif" }}>Attorney Exchange</h1>
                <Badge className="bg-gradient-to-r from-blue-700 to-indigo-800">MARKETPLACE</Badge>
              </div>
              <p className="text-muted-foreground">Post jobs, find contract attorneys, AI-powered matching</p>
            </div>
            <Button onClick={() => setShowPostJob(true)} className="gap-2">
              <Plus className="h-4 w-4" /> Post a Job
            </Button>
          </div>
        </div>
        
        {/* Stats Bar */}
        {stats && (
          <div className="grid grid-cols-5 gap-4 mb-6">
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-blue-600">{stats.open_jobs}</p>
                <p className="text-sm text-muted-foreground">Open Jobs</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-emerald-600">{stats.active_attorneys}</p>
                <p className="text-sm text-muted-foreground">Active Attorneys</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold">{stats.completed_jobs}</p>
                <p className="text-sm text-muted-foreground">Completed</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-violet-600">${Math.round(stats.total_transaction_volume).toLocaleString()}</p>
                <p className="text-sm text-muted-foreground">Volume</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-2xl font-bold text-amber-600">{stats.platform_fee_rate}</p>
                <p className="text-sm text-muted-foreground">Platform Fee</p>
              </CardContent>
            </Card>
          </div>
        )}
        
        {/* Jobs List */}
        <Card>
          <CardHeader>
            <CardTitle>Open Jobs</CardTitle>
            <CardDescription>Browse available legal work opportunities</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-10">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : jobs.length > 0 ? (
              <div className="space-y-4">
                {jobs.map((job, i) => (
                  <div key={i} className="p-4 rounded-lg border hover:shadow-md transition-shadow">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h3 className="font-semibold text-lg">{job.title}</h3>
                        <p className="text-sm text-muted-foreground">{job.firm_name} • {job.jurisdiction}</p>
                      </div>
                      <div className="flex gap-2">
                        <Badge variant={job.urgency === 'urgent' ? 'destructive' : job.urgency === 'asap' ? 'default' : 'secondary'}>
                          {job.urgency}
                        </Badge>
                        <Badge variant="outline">{job.job_type.replace(/_/g, ' ')}</Badge>
                      </div>
                    </div>
                    <p className="text-sm mb-3 line-clamp-2">{job.description}</p>
                    <div className="flex justify-between items-center">
                      <div className="flex gap-4 text-sm text-muted-foreground">
                        <span><strong>Budget:</strong> ${job.budget_min}-${job.budget_max}/hr</span>
                        {job.estimated_hours && <span><strong>Est Hours:</strong> {job.estimated_hours}</span>}
                        {job.court && <span><strong>Court:</strong> {job.court}</span>}
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline">View Details</Button>
                        <Button size="sm">Apply Now</Button>
                      </div>
                    </div>
                    {job.required_skills?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-3">
                        {job.required_skills.map((skill, j) => (
                          <Badge key={j} variant="outline" className="text-xs">{skill}</Badge>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-10">
                <Building2 className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground mb-4">No open jobs yet. Be the first to post!</p>
                <Button onClick={() => setShowPostJob(true)}>Post a Job</Button>
              </div>
            )}
          </CardContent>
        </Card>
        
        {/* Post Job Modal */}
        {showPostJob && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <Card className="w-full max-w-3xl max-h-[90vh] overflow-y-auto">
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle>Post a Job</CardTitle>
                  <Button variant="ghost" size="sm" onClick={() => setShowPostJob(false)}><X className="h-4 w-4" /></Button>
                </div>
                <CardDescription>Find the perfect contract attorney for your case</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Job Title *</Label>
                  <Input value={jobForm.title} onChange={e => setJobForm({...jobForm, title: e.target.value})} placeholder="e.g., Court Appearance - LA Superior Court" />
                </div>
                
                <div>
                  <Label>Description *</Label>
                  <Textarea value={jobForm.description} onChange={e => setJobForm({...jobForm, description: e.target.value})} placeholder="Describe the work needed..." rows={3} />
                </div>
                
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>Job Type</Label>
                    <Select value={jobForm.job_type} onValueChange={v => setJobForm({...jobForm, job_type: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {JOB_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Case Type</Label>
                    <Select value={jobForm.case_type} onValueChange={v => setJobForm({...jobForm, case_type: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="personal_injury">Personal Injury</SelectItem>
                        <SelectItem value="employment">Employment</SelectItem>
                        <SelectItem value="contract">Contract</SelectItem>
                        <SelectItem value="family">Family</SelectItem>
                        <SelectItem value="criminal">Criminal</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Urgency</Label>
                    <Select value={jobForm.urgency} onValueChange={v => setJobForm({...jobForm, urgency: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="normal">Normal</SelectItem>
                        <SelectItem value="urgent">Urgent</SelectItem>
                        <SelectItem value="asap">ASAP</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Jurisdiction *</Label>
                    <Input value={jobForm.jurisdiction} onChange={e => setJobForm({...jobForm, jurisdiction: e.target.value})} placeholder="e.g., California" />
                  </div>
                  <div>
                    <Label>Court</Label>
                    <Input value={jobForm.court} onChange={e => setJobForm({...jobForm, court: e.target.value})} placeholder="e.g., LA Superior Court" />
                  </div>
                </div>
                
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>Budget Min ($/hr)</Label>
                    <Input type="number" value={jobForm.budget_min} onChange={e => setJobForm({...jobForm, budget_min: parseInt(e.target.value) || 0})} />
                  </div>
                  <div>
                    <Label>Budget Max ($/hr)</Label>
                    <Input type="number" value={jobForm.budget_max} onChange={e => setJobForm({...jobForm, budget_max: parseInt(e.target.value) || 0})} />
                  </div>
                  <div>
                    <Label>Estimated Hours</Label>
                    <Input type="number" value={jobForm.estimated_hours} onChange={e => setJobForm({...jobForm, estimated_hours: parseInt(e.target.value) || 0})} />
                  </div>
                </div>
                
                <div>
                  <Label>Required Bar States</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {["CA", "NY", "TX", "FL", "IL", "PA", "OH", "GA", "NC", "NJ"].map(state => (
                      <Badge 
                        key={state} 
                        variant={jobForm.required_bar_states.includes(state) ? "default" : "outline"}
                        className="cursor-pointer"
                        onClick={() => toggleState(state)}
                      >
                        {state}
                      </Badge>
                    ))}
                  </div>
                </div>
                
                <div>
                  <Label>Required Skills</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {["motion_practice", "deposition", "trial", "negotiation", "research", "drafting"].map(skill => (
                      <Badge 
                        key={skill} 
                        variant={jobForm.required_skills.includes(skill) ? "default" : "outline"}
                        className="cursor-pointer"
                        onClick={() => toggleSkill(skill)}
                      >
                        {skill.replace(/_/g, ' ')}
                      </Badge>
                    ))}
                  </div>
                </div>
                
                <Button onClick={postJob} disabled={posting || !jobForm.title || !jobForm.description || !jobForm.jurisdiction} className="w-full">
                  {posting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
                  Post Job
                </Button>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
      <Footer />
    </main>
  );
};


export default AttorneyExchangePage;
